import {
  PipelineFilters,
  PipelineNotificationContext,
} from '@/modules/pipelines/contexts/pipeline-notification-context';
import { useContext, useRef } from 'react';
import { ToggleButton, ToggleButtonGroup } from 'react-bootstrap';
import { Form } from 'react-router-dom';
import PipelineEntityFilterControl from './pipeline-entity-filter-control';
import { toggleMap } from './badges/pipeline-color-map';
import dayjs from 'dayjs';

type FilterKeys = Exclude<keyof PipelineFilters, 'postedBetween' | 'eventType'>;

export default function PipelineFilterDropdown() {
  const { events, entities, eventStatuses, planningStatuses, filters, setFilters } = useContext(
    PipelineNotificationContext,
  );
  const formRef = useRef<HTMLFormElement>(null);
  const afterPickerRef = useRef<HTMLInputElement>(null);
  const beforePickerRef = useRef<HTMLInputElement>(null);

  const startDate = filters?.postedBetween?.start
    ? new Date(filters.postedBetween.start)
    : undefined;
  const endDate = filters?.postedBetween?.end ? new Date(filters.postedBetween.end) : undefined;

  const handleDateChange = (type: 'start' | 'end', date?: Date) => {
    setFilters(prev => {
      const newDate = { ...prev.postedBetween, [type]: date || undefined };
      if (!(newDate.start || newDate.end)) {
        const { postedBetween, ...rest } = prev;
        return rest;
      }
      return { ...prev, postedBetween: newDate };
    });
  };

  return (
    <Form ref={formRef}>
      <div className="d-flex gap-4 mb-2">
        {[
          {
            title: 'Nature',
            key: 'planningStatus' as FilterKeys,
            values: planningStatuses,
          },
          {
            title: 'Status',
            key: 'eventStatus' as FilterKeys,
            values: eventStatuses,
          },
        ].map(d => (
          <div className="d-flex flex-column gap-1">
            <span className="text-text-dark fw-semibold" style={{ fontSize: '0.9rem' }}>
              {d.title}
            </span>
            <ToggleButtonGroup
              type="checkbox"
              className="d-flex flex-wrap gap-2"
              value={(Array.isArray(filters[d.key]) ? filters[d.key] : []) as string[]}
              onChange={(next: string[]) => {
                setFilters(prev => ({
                  ...prev,
                  [d.key]: next.length ? next : undefined,
                }));
              }}
            >
              {d.values.map((v, i) => (
                <ToggleButton
                  size="sm"
                  key={`${d.key}-${i}`}
                  id={`${d.key}-${i}`}
                  value={v}
                  variant={toggleMap[v]}
                  className={`rounded-pill px-3`}
                >
                  {v.charAt(0).toUpperCase() + v.slice(1)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </div>
        ))}

        <div className="d-flex flex-column gap-1">
          <span className="text-text-dark fw-semibold" style={{ fontSize: '0.9rem' }}>
            Urgency
          </span>
          <ToggleButtonGroup
            type="checkbox"
            className="d-flex flex-wrap gap-2"
            value={filters.isCritical !== undefined ? [filters.isCritical] : []}
            onChange={(next: boolean[]) => {
              const opposite = next.at(-1);
              setFilters(prev => ({
                ...prev,
                isCritical: prev.isCritical === opposite ? undefined : opposite,
              }));
            }}
          >
            <ToggleButton
              size="sm"
              id="crit-yes"
              value="true"
              variant="outline-warning"
              className={`rounded-pill px-3`}
            >
              Critical
            </ToggleButton>
            <ToggleButton
              size="sm"
              id="crit-no"
              value="false"
              variant="outline-warning"
              className={`rounded-pill px-3`}
            >
              Non-Critical
            </ToggleButton>
          </ToggleButtonGroup>
        </div>
      </div>

      <div className="d-flex flex-wrap gap-1 mb-1 w-100">
        <div style={{ flex: '1 1 300px' }}>
          <PipelineEntityFilterControl
            filters={filters}
            setFilters={setFilters}
            items={entities}
            formRef={formRef}
          />
        </div>

        <div style={{ flex: '1 1 300px' }}>
          <PipelineEntityFilterControl
            filters={filters}
            setFilters={setFilters}
            items={events}
            formRef={formRef}
          />
        </div>

        <div style={{ flex: '1 1 300px' }}>
          <div className="d-flex gap-1 w-100">
            {['After', 'Before'].map(label => {
              const isStart = label === 'After';

              const date = isStart ? startDate : endDate;
              const dateString = date instanceof Date ? dayjs(date).format('YYYY-MM-DD') : '';

              const type = isStart ? 'start' : 'end';
              const ref = isStart ? afterPickerRef : beforePickerRef;

              return (
                <div className="w-100" key={label}>
                  <span className="text-text-dark fw-semibold" style={{ fontSize: '0.9rem' }}>
                    {label}
                  </span>
                  <input
                    ref={ref}
                    type="date"
                    className="form-control form-control-sm"
                    value={dateString}
                    onClick={() => {
                      if (ref.current !== null) ref.current.showPicker();
                    }}
                    onChange={e => {
                      const val = e.target.value;
                      if (!val) {
                        handleDateChange(type, undefined);
                        return;
                      }

                      const dateVal = isStart
                        ? dayjs(val).startOf('day').toDate()
                        : dayjs(val).endOf('day').toDate();
                      handleDateChange(type, dateVal);
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Form>
  );
}
